import React from 'react';
import {
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  InformationCircleIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import PageLoader from '../../components/PageLoader';
import InboundConfigurationDeleteModal from './InboundConfigurationDeleteModal';
import { useInboundConfigurationPage } from './useInboundConfigurationPage';

const InboundConfigurationPage: React.FC = () => {
  const {
    campaignContext,
    setCampaignContext,
    isActive,
    setIsActive,
    loading,
    saving,
    deleting,
    error,
    success,
    existingConfig,
    showDeleteModal,
    handleSave,
    openDeleteModal,
    closeDeleteModal,
    handleDelete,
  } = useInboundConfigurationPage();

  if (loading) {
    return <PageLoader message="Loading configuration..." />;
  }

  return (
    <div className="relative">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -right-64 -top-64 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full"></div>
        <div className="absolute -left-64 -bottom-64 w-[800px] h-[800px] bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
              <CogIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-50">
              Inbound Call Configuration
            </h1>
          </div>
          <p className="text-slate-300 text-lg">
            Define how your Gateway phone number handles incoming calls.
          </p>
        </div>

        <div>
          <div className="max-w-6xl">
            <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl p-6 border border-slate-700/50">
              <h2 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <InformationCircleIcon className="h-6 w-6 text-cyan-400 mr-2" />
                Configuration Details
              </h2>

              {error && (
                <div className="bg-red-500/20 border border-red-400 text-red-100 px-4 py-3 rounded-lg relative mb-4 flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  <span className="block sm:inline">{error}</span>
                </div>
              )}
              {success && (
                <div className="bg-green-500/20 border border-green-400 text-green-100 px-4 py-3 rounded-lg relative mb-4 flex items-center">
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  <span className="block sm:inline">{success}</span>
                </div>
              )}

              <div className="mb-6">
                <label htmlFor="campaignContext" className="block text-sm font-medium text-slate-300 mb-2">
                  Campaign Context (System Message)
                </label>
                <textarea
                  id="campaignContext"
                  name="campaignContext"
                  rows={6}
                  className="mt-1 block w-full px-4 py-3 shadow-sm sm:text-sm bg-white/5 border border-white/10 text-white placeholder-gray-400 rounded-md focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                  placeholder="Provide a system message for the assistant, e.g., 'You are a friendly sales agent calling about a new offer...'"
                  value={campaignContext}
                  onChange={(e) => setCampaignContext(e.target.value)}
                  disabled={saving || deleting}
                ></textarea>
                <p className="mt-2 text-sm text-slate-400">
                  This context will be used as the system message for the selected assistant when handling inbound calls.
                </p>
              </div>

              <div className="flex items-center justify-between mb-6">
                <label htmlFor="isActive" className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="isActive"
                      className="sr-only"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      disabled={saving || deleting}
                    />
                    <div className={`block w-10 h-5 rounded-full ${isActive ? 'bg-cyan-500' : 'bg-gray-600'} transition-colors duration-200`}></div>
                    <div className={`dot absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${isActive ? 'translate-x-5' : ''}`}></div>
                  </div>
                  <span className="ml-3 text-sm font-medium text-slate-300">
                    {isActive ? 'Configuration is Active' : 'Configuration is Inactive'}
                  </span>
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                {existingConfig && (
                  <button
                    onClick={openDeleteModal}
                    disabled={deleting || saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                  >
                    <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
                    Delete Configuration
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || deleting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all duration-200"
                >
                  {saving ? (
                    <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                  ) : (
                    <SparklesIcon className="-ml-1 mr-2 h-5 w-5" />
                  )}
                  {saving ? 'Saving...' : (existingConfig ? 'Update Configuration' : 'Save Configuration')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InboundConfigurationDeleteModal
        isOpen={showDeleteModal}
        config={existingConfig}
        deleting={deleting}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default InboundConfigurationPage;
